import { compact } from 'lodash'
import moment from 'moment/moment'

import { IconNameAndCategoryString } from '../components/Icon'
import { PSApplication } from '../components/app/PSApplication'
import { ItemOption, Weekday } from '../components/settings/SetupBusinessHoursModalV1'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import {
    BookableColorKey,
    getDefaultClassColorKey,
    getDefaultReservationColorKey,
    getDefaultServiceColorKey,
} from '../utils/calendarColors'
import { formatTimeStamp } from '../utils/dates'
import { currencyFloat, formatDuration } from '../utils/numbers'
import { BaseRecord } from './BaseRecord'
import { Bookable, ScheduleState } from './Bookable'
import { CacheFactory } from './CacheFactory'
import { Contract } from './Contract'
import { Gift } from './Gift'
import { Image } from './Image'
import { ItemComponent, ItemComponentConstructorArgs } from './ItemComponent'
import { Lesson } from './Lesson'
import { Mappable } from './Mappable'
import { Option } from './Option'
import { Package } from './Package'
import { Person } from './Person'
import { RecordFactory } from './RecordFactory'
import { Requirement, RequirementConstructorArgs } from './Requirement'
import { FKType, SQLBuilder } from './SQLBuilder'
import { SQLiteFactory } from './SQLiteFactory'
import { Type } from './Type'
import { User } from './User'

export type ItemConstructorArgs = Omit<
    ConstructorArgs<() => Item>,
    'allow_sales_tax' | 'addons' | 'lessons' | 'requirements' | 'components'
> & {
    allow_salestax: boolean | null
    addons: string[] | null
    lessons: Lesson[] | string[] | null
    requirements: string | Requirement[] | RequirementConstructorArgs[] | null
    components: ItemComponentConstructorArgs[] | ItemComponent[] | null
}

export class Item extends BaseRecord implements Bookable, Mappable {
    readonly recordType = 'item'

    readonly iBookable = true
    readonly iMappable = true

    type?: string
    name?: string
    color?: string
    memo?: string
    image?: Image
    chargeBy: 'day' | 'night' | null
    rate?: number
    deposit?: number
    discount?: number
    discountType?: string
    discountInstances?: number // Integer

    seqnum?: number // Integer
    duration?: number // Integer
    isPublic?: boolean
    clientSelectsLocation?: boolean
    isVirtual?: boolean
    royaltyType?: string
    classType?: string
    classCount?: number // Integer
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment
    longitude?: number
    latitude?: number
    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
    countryCode?: string
    private _location?: string
    packageAll?: boolean
    allowGifting?: boolean
    allowSalesTax?: boolean
    allowInventory?: boolean
    allowOutOfStock?: boolean
    allowAddon?: boolean
    allowWaitlist?: boolean // YF: true if this class supports a wait list
    allowConference?: boolean // YF: true if video conferencing should be defaulted on when booking this item
    conferenceType?: string
    readonly conferenceUrlString?: string
    edition?: string
    editionItem?: string
    onboardingFormID?: string
    onboardingContractID?: string
    quantity?: number // YF: only used for adding products to specify quantity on hand
    cost?: number
    addonAll?: boolean // YF: if True, then this item can be associated with all add-on products
    addons: Item[] = [] // YF: list of add-ons to restrict this item to
    lessons: Lesson[] = [] // YF: list of lesson instances [single or series] for newly added classes
    readonly lessonCountString?: string
    components: ItemComponent[] = [] // YF: list of components for this service, class, reservation or product
    weight?: number

    /* scheduling availability fields */
    leadtime?: string
    frequency?: string
    schedule?: number // Integer
    wom?: number // Integer
    interval?: number // Integer
    instances?: number // Integer

    /* package scheduling fields */
    expires?: number // Integer

    /* class scheduling fields (capacity also used with packages to set # of bookings) */
    capacity?: number // Integer
    capacityType?: string
    startDate?: moment.Moment
    endDate?: moment.Moment

    checkinType: 'any' | 'fixed' | 'flex' | null
    checkoutType: 'any' | 'fixed' | 'flex' | null

    checkinTime: number | null

    checkoutTime: number | null
    checkoutStartTime: number | null
    checkoutEndTime: number | null

    checkinDuration: number | null
    checkoutDuration: number | null

    monEndHour?: number
    tueEndHour?: number
    wedEndHour?: number
    thuEndHour?: number
    friEndHour?: number
    satEndHour?: number
    sunEndHour?: number
    monStartHour?: number
    tueStartHour?: number
    wedStartHour?: number
    thuStartHour?: number
    friStartHour?: number
    satStartHour?: number
    sunStartHour?: number
    requirements: Requirement[] = []

    static RESERVATION_CAPACITY = 40

    constructor(data?: Partial<ItemConstructorArgs>) {
        super(data)
        if (!data) {
            this.type = 'service'
            this.frequency = 'weekly'
            this.isPublic = true
            this.clientSelectsLocation = false
            this.isVirtual = false
            this.allowSalesTax = true
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            this.chargeBy = 'day'
            this.checkinType = null
            this.checkoutType = null
            this.checkinTime = null
            this.checkoutTime = null
            this.checkoutStartTime = null
            this.checkoutEndTime = null
            this.checkinDuration = null
            this.checkoutDuration = null
            return
        }

        this.name = data.name ?? undefined
        this.memo = data.memo ?? undefined
        this.duration = this.propertyFromIntArg(data.duration)
        this.rate = this.propertyFromCurrencyArg(data.rate)
        this.weight = this.propertyFromNumberArg(data.weight)
        this.deposit = this.propertyFromCurrencyArg(data.deposit)
        this.discountType = data.discount_type ?? undefined
        this.discount = this.propertyFromCurrencyArg(data.discount)
        this.discountInstances = this.propertyFromNumberArg(data.discount_instances)
        this.seqnum = this.propertyFromIntArg(data.seqnum)
        this.isPublic = data.is_public ?? false
        this.clientSelectsLocation = data.client_selects_location ?? false
        this.isVirtual = data.is_virtual ?? false
        this.classType = data.class_type ?? undefined
        this.classCount = this.propertyFromIntArg(data.class_count)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.leadtime = data.leadtime ?? undefined
        this._location = data.location ?? undefined
        this.line1 = data.line1 ?? undefined
        this.line2 = data.line2 ?? undefined
        this.city = data.city ?? undefined
        this.state = data.state ?? undefined
        this.postalCode = data.postal_code ?? undefined
        this.countryCode = data.country_code ?? undefined
        this.longitude = this.propertyFromNumberArg(data.longitude)
        this.latitude = this.propertyFromNumberArg(data.latitude)
        this.wom = this.propertyFromIntArg(data.wom)
        this.schedule = this.propertyFromIntArg(data.schedule)
        this.monStartHour = this.propertyFromNumberArg(data.mon_start_hour)
        this.tueStartHour = this.propertyFromNumberArg(data.tue_start_hour)
        this.wedStartHour = this.propertyFromNumberArg(data.wed_start_hour)
        this.thuStartHour = this.propertyFromNumberArg(data.thu_start_hour)
        this.friStartHour = this.propertyFromNumberArg(data.fri_start_hour)
        this.satStartHour = this.propertyFromNumberArg(data.sat_start_hour)
        this.sunStartHour = this.propertyFromNumberArg(data.sun_start_hour)
        this.monEndHour = this.propertyFromNumberArg(data.mon_end_hour)
        this.tueEndHour = this.propertyFromNumberArg(data.tue_end_hour)
        this.wedEndHour = this.propertyFromNumberArg(data.wed_end_hour)
        this.thuEndHour = this.propertyFromNumberArg(data.thu_end_hour)
        this.friEndHour = this.propertyFromNumberArg(data.fri_end_hour)
        this.satEndHour = this.propertyFromNumberArg(data.sat_end_hour)
        this.sunEndHour = this.propertyFromNumberArg(data.sun_end_hour)
        this.requirements = (() => {
            if (data.requirements === undefined || data.requirements === null) {
                return []
            }

            if (typeof data.requirements === 'string') {
                const requirement = this.getForeignRecord('requirements', Requirement)
                return requirement !== undefined ? [requirement] : []
            }

            return data.requirements.map((requirement) => {
                if (requirement instanceof Requirement) {
                    return requirement
                }
                return new Requirement(requirement)
            })
        })()
        this.type = data.type ?? undefined
        this.color = data.color
            ? data.color
            : this.type === 'service'
              ? getDefaultServiceColorKey()
              : this.type === 'class'
                ? getDefaultClassColorKey()
                : this.type === 'reservation'
                  ? getDefaultReservationColorKey()
                  : undefined
        this.royaltyType = data.royalty_type ?? undefined
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
        this.chargeBy = data.charge_by ?? null
        this.checkinType = data.checkin_type ?? null
        this.checkoutType = data.checkout_type ?? null
        this.checkinTime = this.propertyFromNumberArg(data.checkin_time) ?? null
        this.checkoutTime = this.propertyFromNumberArg(data.checkout_time) ?? null
        this.checkoutStartTime = this.propertyFromNumberArg(data.checkout_start_time) ?? null
        this.checkoutEndTime = this.propertyFromNumberArg(data.checkout_end_time) ?? null
        this.checkinDuration = this.propertyFromIntArg(data.checkin_duration) ?? null
        this.checkoutDuration = this.propertyFromIntArg(data.checkout_duration) ?? null
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.onboardingFormID = data.onboarding_form_id ?? undefined
        this.onboardingContractID = data.onboarding_contract_id ?? undefined
        this.packageAll = data.package_all ?? false
        this.allowGifting = data.allow_gifting ?? false
        this.allowSalesTax = data.allow_salestax ?? true
        this.allowInventory = data.allow_inventory ?? false
        this.allowOutOfStock = data.allow_out_of_stock ?? false
        this.addonAll = data.addon_all ?? false
        this.allowAddon = data.allow_addon ?? false
        this.allowWaitlist = data.allow_waitlist ?? false
        this.allowConference = data.allow_conference ?? false
        this.conferenceType = data.conference_type ?? undefined
        this.conferenceUrlString = data.conference_url_string ?? undefined
        this.addons = (data.addons ?? []).map((id) =>
            CacheFactory.getInstance().getRecord(Item, id)
        )
        this.lessons = (data.lessons ?? []).map((l) => {
            if (typeof l === 'string') {
                return CacheFactory.getInstance().getRecord(Lesson, l)
            }
            return l
        })
        this.lessonCountString = data.lesson_count_string ?? undefined
        this.components = (data.components ?? []).map((component) =>
            component instanceof ItemComponent ? component : new ItemComponent(component)
        )
        this.frequency = data.frequency ?? undefined
        this.interval = this.propertyFromIntArg(data.interval)
        this.instances = this.propertyFromIntArg(data.instances)
        this.expires = this.propertyFromIntArg(data.expires)
        this.capacity = this.propertyFromIntArg(data.capacity)
        this.capacityType = data.capacity_type ?? undefined
        this.startDate = this.propertyFromTimestampArg(data.start_date)
        this.endDate = this.propertyFromTimestampArg(data.end_date)
        this.edition = data.edition ?? undefined
        this.editionItem = data.edition_item ?? undefined
    }

    clone(overrides?: Partial<ItemConstructorArgs>): Item {
        return new Item({
            ...this.serializeForClone(),
            allow_salestax: this.allowSalesTax,
            addons: compact(this.addons.map((addon) => addon.id)),
            ...overrides,
        })
    }

    static initWithId(id: string): Item {
        return new Item({ id })
    }

    get amount(): number | undefined {
        const discount = this.discount ?? 0
        const rate = this.rate ?? 0
        if (discount > 0 && rate > 0) return rate - discount * currencyFloat(rate / 100)
        return this.rate
    }

    get discountAmount(): number | undefined {
        if (
            this.rate === undefined ||
            this.discount === undefined ||
            this.discountType === undefined
        ) {
            return undefined
        }

        switch (this.discountType) {
            case 'percent': {
                return (this.rate * this.discount) / 100
            }
            case 'rate': {
                return this.discount
            }
            default:
                return undefined
        }
    }

    get discountDescription(): string | undefined {
        if (this.discount === undefined || this.discountType === undefined) {
            return undefined
        }

        switch (this.discountType) {
            case 'percent': {
                return `${this.discount}%`
            }
            case 'rate': {
                return `$${this.discount}`
            }
            default:
                return undefined
        }
    }

    async reserved(): Promise<number> {
        if (this.id) {
            const sql = new SQLBuilder()
            sql.addField('count(*) as ct')
            sql.addTable('bookings')
            sql.filterBy('item = ?', this.id)
            sql.filterBy('cancelledDate is NULL')
            const result = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
            return parseInt(result['ct'])
        }
        return 0
    }

    async capacityStatus(): Promise<string> {
        let remaining = this.capacity ?? 0
        const enrollment = await this.reserved()
        remaining -= enrollment
        return remaining <= 0
            ? 'Reservation is full'
            : remaining === 1
              ? '1 spot left'
              : `${remaining} spots left`
    }

    async fullBooked(): Promise<boolean> {
        return !!this.id && !!this.capacity && this.capacity <= (await this.reserved())
    }

    get dropin(): boolean {
        return 'dropin' === this.classType
    }

    get location(): string | undefined {
        return Person.locationString(
            this.line1,
            this.line2,
            this.city,
            this.state,
            this.postalCode,
            this._location
        )
    }

    get title(): string | undefined {
        return this.name
    }

    get shortTitle(): string | undefined {
        return this.name
    }

    getCoordinate(): [number, number] | undefined {
        if (this.longitude !== undefined && this.latitude !== undefined)
            return [this.longitude, this.latitude]
        return undefined
    }

    set location(location: string | undefined) {
        this._location = location
    }

    hasEqualSchedule(item?: Item): boolean {
        return (
            this.monStartHour === item?.monStartHour &&
            this.tueStartHour === item?.tueStartHour &&
            this.wedStartHour === item?.wedStartHour &&
            this.thuStartHour === item?.thuStartHour &&
            this.friStartHour === item?.friStartHour &&
            this.satStartHour === item?.satStartHour &&
            this.sunStartHour === item?.sunStartHour &&
            this.monEndHour === item?.monEndHour &&
            this.tueEndHour === item?.tueEndHour &&
            this.wedEndHour === item?.wedEndHour &&
            this.thuEndHour === item?.thuEndHour &&
            this.friEndHour === item?.friEndHour &&
            this.satEndHour === item?.satEndHour &&
            this.sunEndHour === item?.sunEndHour
        )
    }

    get person(): User | undefined {
        return this.employee ?? this.owner
    }

    get date(): moment.Moment | undefined {
        return this.startDate
    }

    set date(date: moment.Moment | undefined) {
        this.startDate = date
    }

    get weekDays(): string[] {
        const l = []
        const schedule = this.schedule ?? 0
        if (schedule) {
            let idx = 1
            for (let i = 0; i < 7; i++) if ((schedule & (idx << i)) !== 0) l.push(String(idx << i))
        }
        return l
    }

    get confirmedDate(): moment.Moment | undefined {
        return !!this.cancelledDate ? undefined : this.createdDate
    }

    set weekDays(week_days: string[]) {
        let schedule = 0
        const intVals = week_days.map((s) => parseInt(s))
        for (const i of intVals) {
            schedule |= i
        }
        this.schedule = schedule
    }

    get requirementsStrings(): string[] {
        if (this.requirements.length === 0) return []
        return this.requirements.map((r) => r.name || '')
    }

    appliesTo(item: Item): boolean {
        if (this.packageAll) return true
        if ('package' === this.type) {
            const components = this.components
            for (const component of components) {
                if (item.equals(component.component)) return true
            }
        }
        return false
    }

    static leadtimeStrings(type?: string): Option[] {
        const strings: Option[] = []
        if ('product' === type) {
            strings.push(new Option(undefined, { id: '', label: 'Same day' }))
            strings.push(new Option(undefined, { id: '24', label: '24 hours' }))
            strings.push(new Option(undefined, { id: '48', label: '2 days' }))
            strings.push(new Option(undefined, { id: '72', label: '3 days' }))
            strings.push(new Option(undefined, { id: '96', label: '4 days' }))
        }
        else {
            strings.push(new Option(undefined, { id: '', label: 'No lead time' }))
            strings.push(new Option(undefined, { id: '15m', label: '15 minutes' }))
            strings.push(new Option(undefined, { id: '30m', label: '30 minutes' }))
            strings.push(new Option(undefined, { id: '45m', label: '45 minutes' }))
            strings.push(new Option(undefined, { id: '1', label: '1 hour' }))
            strings.push(new Option(undefined, { id: '2', label: '2 hours' }))
            strings.push(new Option(undefined, { id: '3', label: '3 hours' }))
            strings.push(new Option(undefined, { id: '4', label: '4 hours' }))
            strings.push(new Option(undefined, { id: '6', label: '6 hours' }))
            strings.push(new Option(undefined, { id: '8', label: '8 hours' }))
            strings.push(new Option(undefined, { id: '12', label: '12 hours' }))
            strings.push(new Option(undefined, { id: 'nextDay', label: 'Next day' }))
            strings.push(new Option(undefined, { id: '24', label: '24 hours' }))
            strings.push(new Option(undefined, { id: '36', label: '36 hours' }))
            strings.push(new Option(undefined, { id: '48', label: '2 days' }))
            strings.push(new Option(undefined, { id: '72', label: '3 days' }))
            strings.push(new Option(undefined, { id: '168', label: 'One week' }))
            strings.push(new Option(undefined, { id: '336', label: 'Two weeks' }))
            strings.push(new Option(undefined, { id: '504', label: 'Three weeks' }))
            strings.push(new Option(undefined, { id: '672', label: 'Four weeks' }))
            strings.push(new Option(undefined, { id: '1008', label: 'Six weeks' }))
            strings.push(new Option(undefined, { id: '1344', label: 'Eight weeks' }))
            strings.push(new Option(undefined, { id: '2016', label: 'Twelve weeks' }))
        }
        if ('class' === type)
            strings.push(new Option(undefined, { id: '-15m', label: '15 minutes (after)' }))
        return strings
    }

    static async videoConferenceStrings(isClass: boolean): Promise<Option[]> {
        const zoomConnected = (await RecordFactory.getInstance().loadCredentials('zoom')).length > 0
        return Item.videoConferenceStringsSync(isClass, zoomConnected)
    }

    static videoConferenceStringsSync(isClass: boolean, zoomConnected: boolean): Option[] {
        const zoomSubtitle = zoomConnected
            ? 'Attach a Zoom link'
            : 'Link Zoom account to use Zoom video conferencing'
        const options: Option[] = []
        options.push(
            new Option(undefined, {
                id: '',
                label: 'Default',
                subtitle: 'No video link',
                enabled: true,
            })
        )
        options.push(
            new Option(undefined, {
                id: 'zoom',
                label: 'Zoom',
                subtitle: zoomSubtitle,
                enabled: zoomConnected,
            })
        )
        if (!isClass) {
            options.push(
                new Option(undefined, {
                    id: 'pocketsuite',
                    label: 'PocketSuite',
                    subtitle: 'Attach a PocketSuite link',
                    enabled: true,
                })
            )
        }
        return options
    }

    static classTypeStrings(): Option[] {
        const strings: Option[] = []
        strings.push(
            new Option(undefined, {
                id: 'dropin',
                label: 'Single Drop-In',
                group: undefined,
                subtitle: 'Clients will book into one class lesson at a time',
            })
        )
        strings.push(
            new Option(undefined, {
                id: 'series',
                label: 'Series',
                group: undefined,
                subtitle: 'Classes start on a fixed schedule & clients are booked into all classes',
            })
        )
        strings.push(
            new Option(undefined, {
                id: 'open',
                label: 'Open Enrollment',
                group: undefined,
                subtitle: 'Classes have a fixed schedule & clients are booked into first available',
            })
        )
        return strings
    }

    static royaltyTypeStrings(): Option[] {
        const strings: Option[] = []
        strings.push(
            new Option(undefined, { id: 'royalty', label: 'Royalty fee', subtitle: 'Royalty fee' })
        )
        strings.push(
            new Option(undefined, {
                id: 'marketing',
                label: 'Marketing fee',
                subtitle: 'Marketing fee',
            })
        )
        strings.push(
            new Option(undefined, { id: 'fixed', label: 'Fixed fee', subtitle: 'Fixed fee' })
        )
        return strings
    }

    static sessionStrings(showCount: boolean, showTime: boolean): Option[] {
        const strings: Option[] = []
        if (showCount) {
            for (let i = 1; i <= Package.PACKAGE_CAPACITY; i++) {
                strings.push(
                    new Option(undefined, {
                        id: i.toString(),
                        label: i.toString(),
                        group: 'Number of sessions',
                    })
                )
            }
            ;['144', '192', '240'].forEach((id: string) => {
                strings.push(
                    new Option(undefined, {
                        id: id,
                        label: id,
                        group: 'Number of sessions',
                    })
                )
            })
        }
        if (showTime) {
            for (let i = 30; i < 10 * 60; i = i + 30) {
                strings.push(
                    new Option(undefined, {
                        id: `${i}h`,
                        label: formatDuration(i, false),
                        group: 'Number of hours',
                    })
                )
            }
            for (let i = 10; i <= Package.PACKAGE_CAPACITY; i++)
                strings.push(
                    new Option(undefined, {
                        id: `${i * 60}h`,
                        label: `${i} hours`,
                        group: 'Number of hours',
                    })
                )
        }
        return strings
    }

    static capacityStrings(minCount: number, maxCount: number): Option[] {
        const strings: Option[] = []
        for (let i = minCount; i <= maxCount; i++)
            strings.push(new Option(undefined, { id: i.toString(), label: i.toString() }))
        return strings
    }

    static serviceCapacityStrings(): Option[] {
        const strings: Option[] = []
        strings.push(
            new Option(undefined, {
                id: '',
                label: 'Same as business',
                subtitle: PSApplication.me().company
                    ? 'Capacity will be based on your team and double booking settings'
                    : 'Capacity will be based on your double booking settings',
                group: undefined,
            })
        )
        strings.push(
            new Option(undefined, {
                id: '1',
                label: 'Single capacity',
                subtitle: 'Only one client can book this equipment / room at a time',
                group: undefined,
            })
        )
        strings.push(...Item.capacityStrings(2, 30))
        return strings
    }

    get locationStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'none', label: 'No location' }))
        if (this.owner?.vendorLocation)
            strings.push(new Option(undefined, { id: 'business', label: 'Business location' }))
        if (this.type !== 'class')
            strings.push(new Option(undefined, { id: 'client', label: 'Client selects location' }))
        strings.push(
            new Option(undefined, {
                id: 'another',
                label: this.location ? this.location : 'Another location',
            })
        )
        return strings
    }

    get expiresString(): string | undefined {
        let l: Option[] = Package.expirationStrings()
        if ('gift' === this.type) l = Gift.expirationStrings()
        const expires = this.expires ?? ''
        for (const opt of l) if (opt.id === expires.toString()) return opt.label
        return undefined
    }

    get leadtimeString(): string | undefined {
        const type = this.type
        if (this.leadtime && type) {
            const strings = Item.leadtimeStrings(type)
            for (const op of strings) {
                const optId = op.id ?? ''
                if (optId.length > 0 && optId === this.leadtime) return op.label
            }
        }
        return undefined
    }

    get weightString(): string | undefined {
        const weight = this.weight ?? 0
        if (weight > 0) return `${weight} lbs`
        return undefined
    }

    /**
     * Return helper icon used to display next to feature name on Enable features screen
     * return icon
     */
    //todo: change reference to R.drawable
    // get icon(): number {
    //     switch (this.type) {
    //         case "subscription":
    //             return R.drawable.dashboard_subscriptions;
    //         case "class":
    //             return R.drawable.dashboard_classes;
    //         case "gift":
    //             return R.drawable.dashboard_gifts;
    //         case "package":
    //             return R.drawable.dashboard_packages;
    //         case "product":
    //             return R.drawable.dashboard_products;
    //         case "service":
    //             return R.drawable.dashboard_services;
    //     }
    //     return R.drawable.dashboard_reservations;
    // }

    get checkInTime(): number | undefined {
        return (
            this.monStartHour ||
            this.tueStartHour ||
            this.wedStartHour ||
            this.thuStartHour ||
            this.friStartHour ||
            this.satStartHour ||
            this.sunStartHour
        )
    }

    get checkOutTime(): number | undefined {
        return (
            this.monEndHour ||
            this.tueEndHour ||
            this.wedEndHour ||
            this.thuEndHour ||
            this.friEndHour ||
            this.satEndHour ||
            this.sunEndHour
        )
    }

    // Bookable interface methods
    get allowJoin(): boolean {
        return false
    }

    get allowCheckin(): boolean {
        return false
    }

    get allowNotify(): boolean {
        return false
    }

    get hasSchedule(): boolean {
        return false
    }

    get isRecurring(): boolean {
        return false
    }

    get ownerIsMe(): boolean {
        return this.owner != undefined && this.owner.id === PSApplication.me().id
    }

    get scheduleStates(): ScheduleState[] {
        return [ScheduleState.Complete]
    }

    setReservationTimes(
        startDate: Date,
        checkIn: number | undefined,
        checkOut: number | undefined,
        duration: number | undefined
    ) {
        let startMoment = moment(startDate)
        if (checkIn) {
            const numCheckIn = Number(checkIn)
            startMoment = startMoment.hour(Math.floor(numCheckIn))
            if (numCheckIn % 1 !== 0) startMoment = startMoment.minute(30)
        }
        this.startDate = startMoment

        switch (startMoment.day()) {
            case 0:
                this.sunStartHour = checkIn
                break
            case 1:
                this.monStartHour = checkIn
                break
            case 2:
                this.tueStartHour = checkIn
                break
            case 3:
                this.wedStartHour = checkIn
                break
            case 4:
                this.thuStartHour = checkIn
                break
            case 5:
                this.friStartHour = checkIn
                break
            case 6:
                this.satStartHour = checkIn
                break
        }

        if (duration) {
            let endMoment = startMoment.clone().add(Number(duration) / 1440, 'days')
            if (checkOut) {
                const numCheckOut = Number(checkOut)
                endMoment = endMoment.hour(Math.floor(numCheckOut))
                if (numCheckOut % 1 !== 0) endMoment = endMoment.minute(30)
            }
            this.endDate = endMoment

            switch (endMoment.day()) {
                case 0:
                    this.sunEndHour = checkOut
                    break
                case 1:
                    this.monEndHour = checkOut
                    break
                case 2:
                    this.tueEndHour = checkOut
                    break
                case 3:
                    this.wedEndHour = checkOut
                    break
                case 4:
                    this.thuEndHour = checkOut
                    break
                case 5:
                    this.friEndHour = checkOut
                    break
                case 6:
                    this.satEndHour = checkOut
                    break
            }
        }
    }

    setStartEndHoursForDay(day: number, checkIn: number | undefined, checkOut: number | undefined) {
        if (day & 1) {
            this.monStartHour = checkIn
            this.tueEndHour = checkOut
        }
        else if (day & 2) {
            this.tueStartHour = checkIn
            this.wedEndHour = checkOut
        }
        else if (day & 4) {
            this.wedStartHour = checkIn
            this.thuEndHour = checkOut
        }
        else if (day & 8) {
            this.thuStartHour = checkIn
            this.friEndHour = checkOut
        }
        else if (day & 16) {
            this.friStartHour = checkIn
            this.satEndHour = checkOut
        }
        else if (day & 32) {
            this.satStartHour = checkIn
            this.sunEndHour = checkOut
        }
        else if (day & 64) {
            this.sunStartHour = checkIn
            this.monEndHour = checkOut
        }
    }

    setDayStartHour(
        day: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun',
        value: number | undefined
    ) {
        switch (day) {
            case 'mon': {
                this.monStartHour = value
                break
            }
            case 'tue': {
                this.tueStartHour = value
                break
            }
            case 'wed': {
                this.wedStartHour = value
                break
            }
            case 'thu': {
                this.thuStartHour = value
                break
            }
            case 'fri': {
                this.friStartHour = value
                break
            }
            case 'sat': {
                this.satStartHour = value
                break
            }
            case 'sun': {
                this.sunStartHour = value
                break
            }
        }
    }

    setDayEndHour(
        day: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun',
        value: number | undefined
    ) {
        switch (day) {
            case 'mon': {
                this.monEndHour = value
                break
            }
            case 'tue': {
                this.tueEndHour = value
                break
            }
            case 'wed': {
                this.wedEndHour = value
                break
            }
            case 'thu': {
                this.thuEndHour = value
                break
            }
            case 'fri': {
                this.friEndHour = value
                break
            }
            case 'sat': {
                this.satEndHour = value
                break
            }
            case 'sun': {
                this.sunEndHour = value
                break
            }
        }
    }

    startHourOnDate(date: moment.Moment): number | undefined {
        switch (date.day()) {
            case 0: // Sunday
                return this.sunStartHour
            case 1: // Monday
                return this.monStartHour
            case 2: // Tuesday
                return this.tueStartHour
            case 3: // Wednesday
                return this.wedStartHour
            case 4: // Thursday
                return this.thuStartHour
            case 5: // Friday
                return this.friStartHour
            case 6: // Saturday
                return this.satStartHour
            default:
                return undefined
        }
    }

    endHourOnDate(date: moment.Moment): number | undefined {
        switch (date.day()) {
            case 0: // Sunday
                return this.sunEndHour
            case 1: // Monday
                return this.monEndHour
            case 2: // Tuesday
                return this.tueEndHour
            case 3: // Wednesday
                return this.wedEndHour
            case 4: // Thursday
                return this.thuEndHour
            case 5: // Friday
                return this.friEndHour
            case 6: // Saturday
                return this.satEndHour
            default:
                return undefined
        }
    }

    // Mappable interface methods
    get coordinate(): [number, number] | undefined {
        return undefined
    }

    get iconConfig():
        | { type: 'image'; uri: string }
        | { type: 'icon'; icon: IconNameAndCategoryString }
        | undefined {
        if (this.image?.uri) {
            return { type: 'image', uri: this.image.uri }
        }
        else if (this.type === 'gift') {
            return { type: 'icon', icon: 'gift.small' }
        }
        else if (this.type === 'package') {
            return { type: 'icon', icon: 'package2.small' }
        }
        else if (this.type === 'service') {
            return { type: 'icon', icon: 'tag.small' }
        }
        else if (this.type === 'product') {
            return { type: 'icon', icon: 'package.small' }
        }
        else if (this.type === 'subscription') {
            return { type: 'icon', icon: 'refresh.small' }
        }
        else if (this.type === 'class') {
            return { type: 'icon', icon: 'student.small' }
        }
        else if (this.type === 'reservation') {
            return { type: 'icon', icon: 'calendarClock.small' }
        }
        else {
            return undefined
        }
    }

    get iconNameAndCategoryString(): IconNameAndCategoryString | undefined {
        switch (this.type) {
            case 'gift': {
                return 'gift.small'
            }
            case 'package': {
                return 'package2.small'
            }
            case 'service': {
                return 'tag.small'
            }
            case 'product': {
                return 'package.small'
            }
            case 'subscription': {
                return 'refresh.small'
            }
            case 'class': {
                return 'student.small'
            }
            case 'reservation': {
                return 'calendarClock.small'
            }
            default:
                return undefined
        }
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.employee) this.employee = BaseRecord.fromSerializer(data.employee, User)
        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.form) this.form = BaseRecord.fromSerializer(data.form, Type)
        if (data.contract) this.contract = BaseRecord.fromSerializer(data.contract, Contract)
        if (data.aPackage) this.aPackage = BaseRecord.fromSerializer(data.aPackage, Item)
        if (data.addons)
            this.addons = data.addons.map((item: any) => BaseRecord.fromSerializer(item, Item))
        if (data.lessons)
            this.lessons = data.lessons.map((lesson: any) =>
                BaseRecord.fromSerializer(lesson, Lesson)
            )
        if (data.components)
            this.components = data.components.map((component: any) =>
                BaseRecord.fromSerializer(component, ItemComponent)
            )
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.startDate) this.startDate = moment(data.startDate)
        if (data.endDate) this.endDate = moment(data.endDate)
        if (data.requirements)
            this.requirements = data.requirements.map((r: any) =>
                BaseRecord.fromSerializer(r, Requirement)
            )
    }

    static fk: FKType = {
        users: ['owner', 'creator', 'employee'],
        images: ['image'],
        types: ['form'],
        contracts: ['contract'],
        items: ['package', 'components', 'addons'],
        columnExtractors: {
            components: 'component',
        },
    }

    static dataType(): DataType {
        return 'items'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['name'] = this.name
        if (this.color !== undefined) dict['color'] = this.color
        if (this.memo !== undefined) dict['memo'] = this.memo
        if (this.quantity !== undefined && this.quantity > 0) dict['quantity'] = this.quantity
        dict['cost'] = this.cost
        dict['employee'] = this.employee?.id ?? ''
        dict['rate'] = this.rate ?? ''
        dict['deposit'] = this.deposit ?? ''
        dict['weight'] = this.weight ?? ''
        dict['discount'] = this.discount
        dict['discount_instances'] =
            this.discountInstances && this.discountInstances !== 0 ? this.discountInstances : ''
        dict['duration'] = this.duration ?? ''
        dict['class_type'] = this.classType ?? ''
        dict['class_count'] = this.classCount ?? 0
        if (this.image?.id !== undefined) dict['image'] = this.image?.id
        else if (this.image?.uri !== undefined) dict['image_uri'] = this.image.uri
        dict['is_public'] = this.isPublic
        dict['client_selects_location'] = this.clientSelectsLocation
        dict['is_virtual'] = this.isVirtual
        dict['royalty_type'] = this.royaltyType ?? ''
        dict['allow_salestax'] = this.allowSalesTax
        dict['allow_inventory'] = this.allowInventory
        dict['allow_out_of_stock'] = this.allowOutOfStock
        dict['addon_all'] = this.addonAll
        dict['allow_addon'] = this.allowAddon
        dict['allow_waitlist'] = this.allowWaitlist
        dict['allow_conference'] = this.allowConference
        dict['conference_type'] = this.conferenceType
        dict['edition'] = this.edition
        dict['edition_item'] = this.editionItem
        dict['start_date'] = formatTimeStamp(this.startDate)
        dict['end_date'] = formatTimeStamp(this.endDate)
        dict['location'] = this.location ?? ''
        dict['line1'] = this.line1 ?? ''
        dict['line2'] = this.line2 ?? ''
        dict['city'] = this.city ?? ''
        dict['state'] = this.state ?? ''
        dict['postal_code'] = this.postalCode ?? ''
        dict['country_code'] = this.countryCode ?? ''
        dict['leadtime'] = this.leadtime ?? ''
        dict['wom'] = this.wom ?? 0
        dict['schedule'] = this.schedule ?? 0
        dict['charge_by'] = this.chargeBy ?? ''
        dict['checkin_type'] = this.checkinType ?? ''
        dict['checkin_time'] = this.checkinTime ?? ''
        dict['checkout_time'] = this.checkoutTime ?? ''
        dict['checkout_start_time'] = this.checkoutStartTime ?? ''
        dict['checkout_end_time'] = this.checkoutEndTime ?? ''
        dict['checkout_type'] = this.checkoutType ?? ''
        dict['checkin_duration'] = this.checkinDuration ?? ''
        dict['checkout_duration'] = this.checkoutDuration ?? ''
        dict['mon_start_hour'] = this.monStartHour ?? ''
        dict['mon_end_hour'] = this.monEndHour ?? ''
        dict['tue_start_hour'] = this.tueStartHour ?? ''
        dict['tue_end_hour'] = this.tueEndHour ?? ''
        dict['wed_start_hour'] = this.wedStartHour ?? ''
        dict['wed_end_hour'] = this.wedEndHour ?? ''
        dict['thu_start_hour'] = this.thuStartHour ?? ''
        dict['thu_end_hour'] = this.thuEndHour ?? ''
        dict['fri_start_hour'] = this.friStartHour ?? ''
        dict['fri_end_hour'] = this.friEndHour ?? ''
        dict['sat_start_hour'] = this.satStartHour ?? ''
        dict['sat_end_hour'] = this.satEndHour ?? ''
        dict['sun_start_hour'] = this.sunStartHour ?? ''
        dict['sun_end_hour'] = this.sunEndHour ?? ''

        if (this.requirements.length > 0) {
            dict['requirements'] = this.requirements.map((requirement: Requirement) => {
                return requirement.id
            })
        }
        dict['type'] = this.type
        dict['form'] = this.form?.id ?? ''
        dict['contract'] = this.contract?.id ?? ''
        if (this.lessons?.length > 0) {
            dict['lessons'] = this.lessons.map((lesson: Lesson) => {
                const lessonData = lesson.jsonObject()
                if (lesson.schedule !== undefined)
                    lessonData['schedule'] = lesson.schedule.jsonObject()
                return lessonData
            })
        }
        dict['package'] = this.aPackage?.id ?? ''
        if (this.type === 'subscription') {
            const item = this.aPackage !== undefined ? this.aPackage : this
            dict['package_all'] = item.packageAll
            dict['components'] = item.components?.map((component: ItemComponent) => {
                return component.jsonObject()
            })
            dict['capacity'] = item.capacity ?? ''
            dict['capacity_type'] = item.capacityType ?? ''
        }
        else if (this.type === 'package') {
            dict['package_all'] = this.packageAll
            dict['components'] = this.components?.map((component: ItemComponent) => {
                return component.jsonObject()
            })
            dict['capacity'] = this.capacity ?? ''
            dict['capacity_type'] = this.capacityType ?? ''
        }
        else if (
            this.type === 'service' ||
            this.type === 'reservation' ||
            this.type === 'class'
        ) {
            dict['addons'] = this.addons.map((product: Item) => {
                return product.id
            })
        }
        if (this.type === 'reservation' || this.type === 'class' || this.type === 'service') {
            dict['capacity'] = this.capacity ?? ''
        }
        dict['frequency'] = this.frequency ?? ''
        dict['interval'] = this.interval && this.interval !== 0 ? this.interval : ''
        dict['instances'] = this.instances && this.instances !== 0 ? this.instances : ''
        dict['expires'] = this.expires ?? ''
        return dict
    }

    get form(): Type | undefined {
        return this.getForeignRecord('form', Type)
    }
    set form(form: Type | undefined) {
        this.setForeignRecord('form', form)
    }
    get contract(): Contract | undefined {
        return this.getForeignRecord('contract', Contract)
    }
    set contract(contract: Contract | undefined) {
        this.setForeignRecord('contract', contract)
    }
    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get employee(): User | undefined {
        return this.getForeignRecord('employee', User)
    }
    set employee(employee: User | undefined) {
        this.setForeignRecord('employee', employee)
    }
    get aPackage(): Item | undefined {
        return this.getForeignRecord('package', Item)
    }
    set aPackage(aPackage: Item | undefined) {
        this.setForeignRecord('package', aPackage)
    }
}

export const createAvailabilityArray = (data: any): Weekday[] => {
    const itemHours: ItemOption[] = []
    for (let i = 0; i < 24; i++) {
        itemHours.push({ value: i, name: moment().hour(i).minute(0).format('h:mma') })
        itemHours.push({ value: i + 0.5, name: moment().hour(i).minute(30).format('h:mma') })
    }

    itemHours.push({ value: 24, name: moment().hour(0).minute(0).format('h:mma') })
    const weekdays: Weekday[] = []
    const dayKeys = [
        { start: 'mon_start_hour', end: 'mon_end_hour' },
        { start: 'tue_start_hour', end: 'tue_end_hour' },
        { start: 'wed_start_hour', end: 'wed_end_hour' },
        { start: 'thu_start_hour', end: 'thu_end_hour' },
        { start: 'fri_start_hour', end: 'fri_end_hour' },
        { start: 'sat_start_hour', end: 'sat_end_hour' },
        { start: 'sun_start_hour', end: 'sun_end_hour' },
    ]

    for (let i = 1; i <= 7; i++) {
        const keys = dayKeys[i - 1]
        const start =
            itemHours.find((i) => i.value.toFixed(1) === parseFloat(data[keys.start]).toFixed(1)) ||
            ''
        const end =
            itemHours.find((i) => i.value.toFixed(1) === parseFloat(data[keys.end]).toFixed(1)) ||
            ''
        const enabled = start !== '' && (data.schedule & (1 << (i - 1))) !== 0

        weekdays.push({
            name: moment().day(i).format('ddd'),
            enabled,
            start,
            end,
            endHours: [],
        })
    }

    return weekdays
}

export const areEqual = (first: Weekday[] | undefined, second: Weekday[] | undefined): boolean => {
    if (!first || !second) {
        const defined = first ?? second
        return !defined?.some((day) => day.enabled)
    }

    if (first?.length !== second?.length) {
        return false
    }

    for (let i = 0; i < first.length; i++) {
        const other = second.find((day) => day.name === first[i].name)
        if (first[i].enabled !== other?.enabled) {
            return false
        }

        if (
            first[i].enabled &&
            (JSON.stringify(first[i].start) !== JSON.stringify(other.start) ||
                JSON.stringify(first[i].end) !== JSON.stringify(other.end))
        ) {
            return false
        }
    }
    return true
}

export const createAvailabilitySummary = (weekdays: Weekday[], fallback?: string): string => {
    const days: string[] = []
    weekdays.forEach((weekday) => {
        if (weekday.enabled) {
            days.push(weekday.name)
        }
    })

    if (days.length === 0) {
        return fallback ?? 'Same as business'
    }

    return days.join(', ')
}
